import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LoadingState,
  Page,
  Table,
  Text,
  Textarea,
} from "@marekl/adminer/components";
import { formatDateTime } from "@marekl/adminer/format";
import { useConfirm } from "@marekl/adminer/hooks";
import { AppTopbar } from "../../../shell/AppTopbar";
import { useCompetition } from "../../../lib/queries/competitions";
import { useTeamMembers } from "../../../lib/queries/memberships";
import { useTeam } from "../../../lib/queries/teams";
import { useStartList } from "../../../lib/queries/startlists";
import { useRuleSetDocument } from "../../../lib/queries/rules";
import { PREVIEW_NOTICE, STAGE_LABELS } from "../../../lib/startListState";
import {
  useDeleteEntry,
  useTeamEntries,
  useWithdrawEntry,
  type Entry,
} from "../../../lib/queries/entries";
import { canEditEntry, entryWindowNotice, withdrawWarning } from "../../../lib/entryWindow";
import { clubEntryBlock, hasRegisteredMember } from "../../../lib/entryEligibility";
import { indexDocument, comboLabel } from "../../../lib/rulesDocument";
import { EntryDetailModal } from "../../../components/EntryDetailModal";
import { BadgeRow } from "../../../components/BadgeRow";
import { CompetitionDeadlines } from "../../../components/CompetitionDeadlines";
import { SeasonRegistrationNotice } from "../../../components/SeasonRegistrationNotice";
import { musicBadge, musicMissingCount, musicWarningItems } from "../../../lib/musicState";
import styles from "./TeamCompetitionEntriesPage.module.css";
import {
  clubCompetitionBadge,
  COMPETITION_TYPE_LABELS,
  daysLeftLabel,
  ENTRY_STATUS_LABELS,
  ENTRY_STATUS_VARIANT,
  formatCompetitionDates,
  isFrozen,
} from "../../../lib/competitionState";
import type { ID } from "../../../lib/id";

type ModalState =
  | { kind: "closed" }
  // The detail holds an id, not a row: uploading music refetches the
  // list, and a snapshot taken when the dialog opened would keep showing
  // „Chybí hudba" over a file that is already stored.
  | { kind: "detail"; id: ID }
  // The odhláška dialog holds the row: its body carries a reason field,
  // so it is a stateful ConfirmDialog rather than useConfirm.
  | { kind: "withdraw"; entry: Entry };

// The team's entries into one competition: the deadline strip, the
// entry list, and the modal that composes a new přihláška.
export function TeamCompetitionEntriesPage() {
  const { id, competitionId } = useParams();
  const teamId = id ?? "";
  const compId = competitionId ?? "";

  const { data: competition, isLoading, error: compError, refetch } = useCompetition(compId);
  const { data: startList } = useStartList(compId);
  const { data: document } = useRuleSetDocument(competition?.rule_set_id);
  const { data: entries = [], isLoading: entriesLoading } = useTeamEntries(teamId, compId);
  // The roster, not `team.state_issues`: the wizard asks the roster, and
  // the server fills state_issues for active teams only.
  const { data: roster = [], isLoading: rosterLoading } = useTeamMembers(teamId);
  const { data: team, isLoading: teamInfoLoading, error: teamError } = useTeam(teamId);
  // Both halves, not just the roster: clubEntryBlock asks the roster whether
  // anybody is registered AND the team whether MČR is its competition at all,
  // so waiting for one of the two answered the second from a default.
  const teamLoading = rosterLoading || teamInfoLoading;
  // A failed team fetch has to reach the error state too: it clears
  // teamInfoLoading with `team` still undefined, and `?? true` below then
  // reads a club outside ČNSMS as ČNSMS and points it at a registration that
  // would refuse it.
  const error = compError ?? teamError;
  const teamIsCmf = team?.federation_is_primary ?? true;
  const remove = useDeleteEntry();
  const withdraw = useWithdrawEntry();
  const confirm = useConfirm();
  const [withdrawNote, setWithdrawNote] = useState("");
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });

  const index = useMemo(() => (document ? indexDocument(document) : null), [document]);

  const crumbs = [
    { label: "Členové týmu", href: `/teams/${teamId}` },
    { label: "Soutěže", href: `/teams/${teamId}/souteze` },
    { label: competition?.name ?? "Soutěž" },
  ];

  if (isLoading) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <LoadingState />
          </Page.Content>
        </Page>
      </>
    );
  }
  if (error || !competition) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <ErrorState title="Soutěž se nepodařilo načíst" error={error} onRetry={refetch} />
          </Page.Content>
        </Page>
      </>
    );
  }

  // Creating needs an open phase; editing and deleting stay possible
  // while the deadline has not passed (a full capacity blocks only new
  // entries). The server enforces both — this only hides dead controls.
  //
  // And a published start list („zveřejněno", not the návrh) closes both,
  // whatever the phase says. The
  // organizer can publish before the uzávěrka, and this page then showed
  // „Pořadatel zveřejnil startovní listinu" directly above a live
  // „Odebrat" — the leader would take their own name off a document the
  // whole hall already holds. The server refuses it now
  // (Compute returns LockStartListPublished); this stops the
  // page offering it.
  const frozen = startList?.published === true;
  const entryOpen = competition.entry_phase === "open" && !frozen;
  // An MČR start admits registered members only, so a team with nobody
  // registered is not offered a přihláška it could never fill; the notice
  // below says why and where to fix it. Waits for the team, or the button
  // would flash in and out.
  const block =
    teamLoading
      ? null
      : clubEntryBlock(competition.type, teamIsCmf, hasRegisteredMember(roster));
  const needsRegistration = block === "needs_registration";
  const canCreate = entryOpen && !teamLoading && block === null;
  // What may be done to an EXISTING přihláška is the server's answer,
  // per row, and never worked out here. Four rungs of the taper crossed
  // with the odhláška, the published listina and the active season are
  // one rule; a second copy of it on this page would drift, and the
  // drift is invisible — a button that saves nothing, or a button
  // missing when it should not be.
  const notice = entryWindowNotice(competition.entry_change_window);
  const badge = clubCompetitionBadge(competition);

  // Music has its OWN window, and it is not the entry one: a published
  // start list does not freeze a sound file, and the uzávěrka hudby
  // commonly falls weeks after the uzávěrka přihlášek. Deriving this
  // from the entry window would lock the club out of fixing a corrupt
  // track for no reason the server would recognise.
  const musicDeadline = competition.effective_music_deadline;
  const musicOpen = !musicDeadline || musicDeadline.isAfter(dayjs());
  const musicDaysLeft = musicDeadline ? daysLeftLabel(musicDeadline) : null;
  const missingMusic = musicMissingCount(entries);

  const close = () => {
    setModal({ kind: "closed" });
    setWithdrawNote("");
    remove.reset();
    withdraw.reset();
  };

  const comboName = (e: Entry) => {
    const ref = index?.combos.get(e.discipline_category_id);
    return ref ? comboLabel(ref) : "—";
  };

  // Resolved from the live list rather than held in state, so an upload
  // that refetches the entries is reflected in the open dialog.
  const detailEntry =
    modal.kind === "detail" ? (entries.find((e) => e.id === modal.id) ?? null) : null;

  return (
    <>
      <AppTopbar crumbs={crumbs} />
      <Page>
        <Page.Header
          title={competition.name}
          subtitle={[
            COMPETITION_TYPE_LABELS[competition.type],
            formatCompetitionDates(competition),
            competition.venue,
          ]
            .filter(Boolean)
            .join(" · ")}
          actions={
            canCreate && (
              // Creating runs through the fullscreen wizard (more room
              // for the roster table); the modal stays the edit surface.
              <Link to={`/teams/${teamId}/souteze/${compId}/nova-prihlaska`}>
                <Button variant="primary">Nová přihláška</Button>
              </Link>
            )
          }
        />
        <Page.Content stack gap="lg">
          <Card>
            <Card.Header
              title="Uzávěrky"
              subtitle={badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
              subtitlePlacement="right"
            />
            <Card.Content>
              {/* The same rows as the Soutěže card: one per distinct
                  instant, so an organizer who set only the uzávěrka
                  přihlášek leaves one date naming everything it closes. */}
              <CompetitionDeadlines competition={competition} />
              {/* One notice for the rung the competition is on, from the
                  server's window rather than from the dates above. */}
              {notice && (
                <Alert variant={notice.variant} className={styles.windowNotice}>
                  {notice.message}
                </Alert>
              )}
            </Card.Content>
          </Card>

          {entryOpen && !teamLoading && needsRegistration && (
            <SeasonRegistrationNotice teamId={teamId} />
          )}

          {/* On the stage, not on having a document at all. A plain leader
              only ever gets a list the clubs may see, but an admin or the
              organizer opening a team's page also reads a draft, and must
              not be told it was published. */}
          {startList && startList.stage !== "draft" && (
            <Card>
              <Card.Header
                title="Startovní listina"
                subtitle={STAGE_LABELS[startList.stage]}
                subtitlePlacement="right"
              />
              <Card.Content>
                {startList.stage === "preview" && <Alert variant="info">{PREVIEW_NOTICE}</Alert>}
                <Text>
                  {startList.stage === "preview"
                    ? "Pořadatel zveřejnil návrh startovní listiny."
                    : "Pořadatel zveřejnil startovní listinu."}{" "}
                  Vaše starty v ní najdete zvýrazněné; když vyberete svůj tým a rozsah „Jen starty týmu“, zbytek se skryje.
                </Text>
                <Link to={`/soutez/${compId}/startovni-listina/tisk`}>
                  <Button variant="primary">Otevřít startovní listinu</Button>
                </Link>
              </Card.Content>
            </Card>
          )}

          {/* Music is owed by the uzávěrka hudby, not by the přihláška: a
              „Chybí hudba" badge on its own read as an entry that was not
              finished. Said once, with the date. */}
          {musicOpen && missingMusic > 0 && (
            <Alert variant="info">
              {`Hudba zatím chybí u ${missingMusic} ${missingMusic === 1 ? "přihlášky" : "přihlášek"}. Nemusíte ji mít hned; stačí ji nahrát přes „Detail“${
                musicDeadline
                  ? ` do uzávěrky hudby ${formatDateTime(musicDeadline)}${musicDaysLeft ? ` (${musicDaysLeft})` : ""}`
                  : ""
              }.`}
            </Alert>
          )}

          <Table<Entry>
            framed
            rows={entries}
            rowKey={(e) => e.id}
            isLoading={entriesLoading}
            columns={[
              {
                key: "cp",
                header: "ČP",
                numeric: true,
                render: (e) => e.entry_number,
              },
              {
                key: "combo",
                header: "Kombinace",
                render: (e) => {
                  const ref = index?.combos.get(e.discipline_category_id);
                  return ref ? comboLabel(ref) : "—";
                },
              },
              {
                key: "competitors",
                header: "Soutěžící",
                render: (e) => (
                  <>
                    {e.competitors.map((c) => (
                      <div key={c.team_membership_id}>
                        {c.person.first_name} {c.person.last_name}
                        {c.kind === "guest" && <Text variant="muted"> (nečlen)</Text>}
                      </div>
                    ))}
                  </>
                ),
              },
              {
                key: "status",
                header: "Stav",
                render: (e) => (
                  <>
                    <Badge variant={ENTRY_STATUS_VARIANT[e.status]}>
                      {ENTRY_STATUS_LABELS[e.status]}
                    </Badge>
                    {e.withdrawn_note && (
                      <Text variant="muted"> {e.withdrawn_note}</Text>
                    )}
                  </>
                ),
              },
              {
                key: "music",
                header: "Hudba",
                render: (e) => {
                  const badge = musicBadge(e.music_source, e.music !== null);
                  return (
                    <BadgeRow>
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                      {/* Each finding as its own badge rather than a
                          sentence: a warning set in muted text next to a
                          green „Nahráno" is exactly what a leader
                          scanning the list skips over. */}
                      {e.music &&
                        musicWarningItems(e.music.warnings).map((w) => (
                          <Badge key={w.kind} variant={w.variant}>
                            {w.label}
                          </Badge>
                        ))}
                    </BadgeRow>
                  );
                },
              },
              {
                key: "actions",
                header: "",
                render: (e) => (
                  <Table.Actions>
                    {/* One action for every row, including a federation
                        or withdrawn one: the dialog is where the roster,
                        the rules and the recording are, and the upload
                        lives inside it. Music therefore stays reachable
                        whatever the entry window says — the two
                        deadlines are different instants and a sound file
                        is not part of the running order. */}
                    <Table.Action appearance="button" onClick={() => setModal({ kind: "detail", id: e.id })}>
                      Detail
                    </Table.Action>
                    {canEditEntry(e.permissions) && (
                      <Table.Action appearance="button" to={`/teams/${teamId}/souteze/${compId}/prihlaska/${e.id}`}>
                        Upravit
                      </Table.Action>
                    )}
                    {/* One removal affordance, whose verb changes at the
                        uzávěrka. Before it nothing has been planned around
                        the entry and it can be re-filed, so it is removed
                        outright; after it a removal has to leave a record,
                        so it becomes an odhláška. The server decides
                        which — offering both at once would let a club
                        create a record it could no longer take back. */}
                    {e.permissions.can_delete && (
                      <Table.Action appearance="button" onClick={() =>
                          confirm({
                            title: "Odebrat přihlášku",
                            description: (
                              <>
                                Opravdu odebrat přihlášku ({comboName(e)})? Do uzávěrky
                                přihlášek nebo naplnění kapacity můžete podat novou.
                              </>
                            ),
                            confirmLabel: "Odebrat",
                            variant: "danger",
                            onConfirm: () => remove.mutateAsync(e.id),
                          })
                        }>
                        Odebrat
                      </Table.Action>
                    )}
                    {e.permissions.can_withdraw && (
                      <Table.Action
                        appearance="button"
                        onClick={() => setModal({ kind: "withdraw", entry: e })}
                      >
                        Odhlásit
                      </Table.Action>
                    )}
                  </Table.Actions>
                ),
              },
            ]}
            empty={
              <EmptyState
                title="Zatím žádná přihláška"
                description={
                  !entryOpen
                    ? "Tým se na tuto soutěž nepřihlásil."
                    : block === "not_our_competition"
                      ? "Na soutěže MČR se přihlašují jen týmy ČNSMS."
                      : needsRegistration
                        ? "Na soutěže MČR se bez registrovaných členů přihlásit nelze."
                      : "Přihlaste soutěžící do disciplín."
                }
                // The header button is above a card and a start-list panel;
                // on an empty page the leader is looking here. Same gate as
                // the header, so the two can never disagree.
                action={
                  canCreate ? (
                    <Link to={`/teams/${teamId}/souteze/${compId}/nova-prihlaska`}>
                      <Button variant="primary">Nová přihláška</Button>
                    </Link>
                  ) : undefined
                }
              />
            }
          />
        </Page.Content>
      </Page>

      {modal.kind === "detail" && (
        <EntryDetailModal
          entry={detailEntry}
          combo={
            detailEntry ? index?.combos.get(detailEntry.discipline_category_id) : undefined
          }
          canUploadMusic={musicOpen && !isFrozen(competition)}
          musicDisabledReason="Uzávěrka hudby už proběhla. Hudbu po uzávěrce nahraje pořadatel soutěže."
          musicDeadline={musicDeadline}
          onClose={close}
        />
      )}

      {modal.kind === "withdraw" && (
        <ConfirmDialog
          open
          title="Odhlásit přihlášku"
          confirmLabel="Odhlásit"
          variant="danger"
          busy={withdraw.isPending}
          error={withdraw.error}
          onClose={close}
          onConfirm={async () => {
            await withdraw.mutateAsync({
              id: modal.entry.id,
              note: withdrawNote.trim(),
            });
            close();
          }}
        >
          <Text>
            Přihláška ČP {modal.entry.entry_number} ({comboName(modal.entry)}) zůstane
            evidovaná jako odhláška; záznam se nemaže.
          </Text>
          {/* Only when it really is final. Before the uzávěrka this row
              offers „Odebrat" instead, so the warning would be a lie —
              and after it, it is the sentence standing between a leader
              and a přihláška they cannot get back. */}
          {withdrawWarning(modal.entry.permissions) && (
            <Alert variant="warn">{withdrawWarning(modal.entry.permissions)}</Alert>
          )}
          <Textarea
            label="Důvod (nepovinné)"
            rows={2}
            value={withdrawNote}
            onChange={(e) => setWithdrawNote(e.target.value)}
          />
        </ConfirmDialog>
      )}
    </>
  );
}
